/**
 * API Configuration
 * 
 * Zentrale Konfiguration für alle API-Aufrufe.
 * Verwendet Environment-Variablen aus .env Dateien:
 * 
 * Development: .env
 * - REACT_APP_API_URL=http://localhost:5174/api
 * 
 * Production: .env.production
 * - REACT_APP_API_URL=https://frankenfloss.net/api
 * 
 * Build-Commands:
 * - npm start → verwendet .env
 * - npm run build → verwendet .env.production
 */

// API Base URL aus Environment-Variable (mit Fallback für Development)
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5174/api';
export const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:5174';

// Helper function to get auth headers
export const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    console.error('No token found in localStorage');
    throw new Error('Nicht authentifiziert. Bitte melden Sie sich erneut an.');
  }
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// FIX 24.08.2026: Automatischer Token-Refresh fuer Komponenten mit rohem fetch().
// Access-Tokens laufen nach 15 Minuten ab. Komponenten, die fetch() direkt mit dem
// localStorage-Token nutzen, liefen danach in 401-Antworten mit leerem Body
// (Kundin-Befunde: Tour-Typ "Fehler beim Speichern", Bulk-Termine ohne Fehlergrund).
// authFetch versucht bei 401 genau einen Refresh (single-flight) und wiederholt den Request.
let refreshInFlight: Promise<boolean> | null = null;

const doRefreshAccessToken = async (): Promise<boolean> => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return false;
  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) return false;
    const json = await response.json();
    const data = json?.data || json;
    const newAccess = data?.accessToken;
    const newRefresh = data?.refreshToken;
    if (!newAccess) return false;
    localStorage.setItem('authToken', newAccess);
    if (newRefresh) localStorage.setItem('refreshToken', newRefresh);
    return true;
  } catch {
    return false;
  }
};

export const refreshAccessToken = (): Promise<boolean> => {
  if (!refreshInFlight) {
    refreshInFlight = doRefreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

export const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const buildHeaders = () => ({
    ...getAuthHeaders(),
    ...(options.headers || {}),
  });
  let response = await fetch(url, { ...options, headers: buildHeaders() });
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      response = await fetch(url, { ...options, headers: buildHeaders() });
    }
  }
  return response;
};

// Fehlertext aus einer Response gewinnen, mit klarer Meldung bei Session-Ablauf
export const responseErrorText = async (response: Response): Promise<string> => {
  if (response.status === 401) {
    return 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.';
  }
  const text = await response.text();
  return text || `Fehler ${response.status}`;
};

// Helper function for API calls with error handling
export const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API call to ${endpoint} failed:`, error);
    throw error;
  }
};

// Debug: Log current configuration (nur in Development)
if (process.env.NODE_ENV === 'development') {
  console.log('API Configuration:', {
    API_BASE_URL,
    BASE_URL,
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  });
}
